import React, { useState, useEffect, useMemo } from 'react';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MONTH_NAMES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

/**
 * Convierte un objeto Date o string YYYY-MM-DD a string YYYY-MM-DD
 */
const toDateString = (date) => {
    if (!date) return '';
    if (typeof date === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
        const d = new Date(date);
        if (!isNaN(d.getTime())) {
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Parsea un string YYYY-MM-DD a Date local (evitando desfase horario UTC)
 */
const parseLocalDate = (str) => {
    if (!str) return null;
    const parts = str.split('-');
    if (parts.length !== 3) return null;
    return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
};

function DualMonthCalendar({
    isOpen,
    minDate,
    maxDate,
    startDate,
    endDate,
    onSelectRange
}) {
    // Mes de referencia para la vista izquierda (primer día del mes)
    const [viewDate, setViewDate] = useState(() => {
        if (endDate) {
            const d = parseLocalDate(endDate);
            if (d) {
                // Si hay fecha final, mostrar el mes anterior en la izquierda para que el mes final quede en la derecha
                return new Date(d.getFullYear(), d.getMonth() - 1, 1);
            }
        }
        if (startDate) {
            const d = parseLocalDate(startDate);
            if (d) return new Date(d.getFullYear(), d.getMonth(), 1);
        }
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() - 1, 1);
    });

    const [hoverDate, setHoverDate] = useState(null);

    // Sincronizar vista si cambian las fechas seleccionadas externamente
    useEffect(() => {
        if (startDate) {
            const d = parseLocalDate(startDate);
            if (d) {
                setViewDate(new Date(d.getFullYear(), d.getMonth(), 1));
            }
        }
    }, [startDate]);

    const minDateStr = useMemo(() => toDateString(minDate), [minDate]);
    const maxDateStr = useMemo(() => toDateString(maxDate), [maxDate]);

    // Mes izquierdo y mes derecho
    const leftMonth = useMemo(() => {
        return new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    }, [viewDate]);

    const rightMonth = useMemo(() => {
        return new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
    }, [viewDate]);

    const canGoPrev = useMemo(() => {
        if (!minDateStr) return true;
        const minD = parseLocalDate(minDateStr);
        if (!minD) return true;
        const firstDayLeft = new Date(leftMonth.getFullYear(), leftMonth.getMonth(), 1);
        return firstDayLeft > new Date(minD.getFullYear(), minD.getMonth(), 1);
    }, [leftMonth, minDateStr]);

    const canGoNext = useMemo(() => {
        if (!maxDateStr) return true;
        const maxD = parseLocalDate(maxDateStr);
        if (!maxD) return true;
        const firstDayRight = new Date(rightMonth.getFullYear(), rightMonth.getMonth(), 1);
        return firstDayRight < new Date(maxD.getFullYear(), maxD.getMonth(), 1);
    }, [rightMonth, maxDateStr]);

    const handlePrevMonth = () => {
        if (!canGoPrev) return;
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        if (!canGoNext) return;
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    // Generar la grilla de días para un mes dado
    const renderMonthGrid = (monthDate) => {
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();
        const firstDayIndex = new Date(year, month, 1).getDay(); // 0: Domingo, 1: Lunes, etc.
        const totalDays = new Date(year, month + 1, 0).getDate();

        const monthName = MONTH_NAMES[month];

        const days = [];
        // Celdas vacías antes del primer día
        for (let i = 0; i < firstDayIndex; i++) {
            days.push(null);
        }
        // Días del mes
        for (let d = 1; d <= totalDays; d++) {
            days.push(d);
        }

        return (
            <div style={{ flex: 1, minWidth: '260px', padding: '0 12px' }}>
                <div style={{
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '15px',
                    color: '#202A52',
                    marginBottom: '1rem'
                }}>
                    {monthName} {year}
                </div>

                {/* Cabecera de días de la semana */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    textAlign: 'center',
                    marginBottom: '8px',
                    color: '#64748b',
                    fontSize: '13px',
                    fontWeight: 500
                }}>
                    {DAYS_OF_WEEK.map((d, i) => (
                        <div key={i} style={{ padding: '4px 0' }}>{d}</div>
                    ))}
                </div>

                {/* Cuadrícula de días */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '2px',
                    rowGap: '6px'
                }}>
                    {days.map((day, idx) => {
                        if (day === null) {
                            return <div key={`empty-${idx}`} style={{ height: '36px' }} />;
                        }

                        const currentDayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        
                        // Validar límites
                        const isBeforeMin = minDateStr && currentDayStr < minDateStr;
                        const isAfterMax = maxDateStr && currentDayStr > maxDateStr;
                        const isDisabled = isBeforeMin || isAfterMax;

                        const isStart = startDate === currentDayStr;
                        const isEnd = endDate === currentDayStr;
                        const isInRange = Boolean(
                            startDate &&
                            endDate &&
                            currentDayStr > startDate &&
                            currentDayStr < endDate
                        );

                        // Si hay startDate pero no endDate, calcular preview con hover
                        const isInHoverRange = Boolean(
                            startDate &&
                            !endDate &&
                            hoverDate &&
                            hoverDate > startDate &&
                            currentDayStr > startDate &&
                            currentDayStr <= hoverDate
                        );

                        let bgColor = 'transparent';
                        let textColor = isDisabled ? '#cbd5e1' : '#334155';
                        let borderRadius = '4px';
                        let fontWeight = 'normal';

                        if (isStart || isEnd) {
                            bgColor = '#202A52';
                            textColor = '#ffffff';
                            borderRadius = '6px';
                            fontWeight = 'bold';
                        } else if (isInRange || isInHoverRange) {
                            bgColor = '#E0F2FE';
                            textColor = '#202A52';
                            borderRadius = '0';
                        }

                        return (
                            <div
                                key={`day-${idx}`}
                                onClick={() => {
                                    if (isDisabled) return;
                                    handleDayClick(currentDayStr);
                                }}
                                onMouseEnter={() => {
                                    if (!isDisabled) setHoverDate(currentDayStr);
                                }}
                                onMouseLeave={() => setHoverDate(null)}
                                style={{
                                    height: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                    fontWeight,
                                    color: textColor,
                                    backgroundColor: bgColor,
                                    borderRadius,
                                    cursor: isDisabled ? 'default' : 'pointer',
                                    userSelect: 'none',
                                    transition: 'background-color 0.15s ease'
                                }}
                            >
                                {day}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const handleDayClick = (dayStr) => {
        if (!startDate || (startDate && endDate)) {
            // Empezar nueva selección
            onSelectRange(dayStr, null);
        } else if (startDate && !endDate) {
            if (dayStr < startDate) {
                // Si seleccionó una fecha anterior, reasignar startDate
                onSelectRange(dayStr, null);
            } else {
                // Completar el rango
                onSelectRange(startDate, dayStr);
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            marginTop: '1.5rem',
            padding: '1.5rem 1rem',
            borderTop: '1px solid #f1f5f9',
            backgroundColor: '#ffffff',
            position: 'relative'
        }}>
            {/* Controles de Navegación de Meses */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'absolute',
                left: '20px',
                right: '20px',
                top: '20px',
                pointerEvents: 'none'
            }}>
                <button
                    type="button"
                    disabled={!canGoPrev}
                    onClick={handlePrevMonth}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: canGoPrev ? 'pointer' : 'default',
                        pointerEvents: 'auto',
                        color: canGoPrev ? '#202A52' : '#cbd5e1',
                        padding: '4px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px'
                    }}
                    title="Mes anterior"
                >
                    <i className="material-icons" style={{ fontSize: '24px' }}>chevron_left</i>
                </button>

                <button
                    type="button"
                    disabled={!canGoNext}
                    onClick={handleNextMonth}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: canGoNext ? 'pointer' : 'default',
                        pointerEvents: 'auto',
                        color: canGoNext ? '#202A52' : '#cbd5e1',
                        padding: '4px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px'
                    }}
                    title="Mes siguiente"
                >
                    <i className="material-icons" style={{ fontSize: '24px' }}>chevron_right</i>
                </button>
            </div>

            {/* Meses en paralelo (vista dual) */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-around',
                gap: '1.5rem'
            }}>
                {renderMonthGrid(leftMonth)}
                {renderMonthGrid(rightMonth)}
            </div>
        </div>
    );
}

export default DualMonthCalendar;
